import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getSuppliers, getAllSuppliers, createSupplier, updateSupplier, deleteSupplier, getStores } from '../api/api';
import { Plus, Edit2, Trash2, Building2, Globe, X } from 'lucide-react';

const Suppliers = () => {
  const { user } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    nit: '',
    store_id: '',
    is_global: true
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = ['admin', 'manager', 'supervisor', 'admin_assistant'].includes(user?.role);

  const loadData = useCallback(async () => {
    try {
      // Cargar proveedores
      let supplierData;
      if (isAdmin) {
        supplierData = await getAllSuppliers();
      } else {
        supplierData = await getSuppliers();
      }
      setSuppliers(supplierData);

      // Cargar tiendas (solo admin)
      if (isAdmin) {
        const storesData = await getStores();
        setStores(storesData);
      }
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setError('Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const data = {
        name: formData.name,
        nit: formData.nit,
        is_global: formData.is_global
      };

      if (!formData.is_global && formData.store_id) {
        data.store_id = formData.store_id;
      }

      if (editingSupplier) {
        await updateSupplier(editingSupplier.id, data);
        setSuccess('Proveedor actualizado exitosamente');
      } else {
        await createSupplier(data);
        setSuccess('Proveedor creado exitosamente');
      }

      setFormData({ name: '', nit: '', store_id: '', is_global: true });
      setShowForm(false);
      setEditingSupplier(null);
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al guardar proveedor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      nit: supplier.nit,
      store_id: supplier.store_id || '',
      is_global: supplier.is_global
    });
    setShowForm(true);
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('¿Está seguro de desactivar este proveedor?')) return;

    try {
      await deleteSupplier(supplierId);
      setSuccess('Proveedor desactivado');
      loadData();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al desactivar proveedor');
    }
  };

  const canEdit = ['admin', 'manager', 'supervisor', 'store_manager', 'purchase_manager'].includes(user?.role);

  return (
    <div className="space-y-8">
      {/* Header futurista */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            Proveedores
          </h1>
          <p className="text-gray-600 mt-2">Gestiona todos los proveedores del sistema</p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditingSupplier(null);
              setFormData({ name: '', nit: '', store_id: '', is_global: isAdmin });
              setShowForm(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center space-x-2 font-semibold"
          >
            <Plus size={22} />
            <span>Nuevo Proveedor</span>
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">⚠️</span>
            <span className="font-medium">{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X size={20} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-xl flex items-center justify-between animate-pulse">
          <div className="flex items-center space-x-3">
            <span className="text-2xl">✅</span>
            <span className="font-medium">{success}</span>
          </div>
          <button onClick={() => setSuccess('')} className="text-green-600 hover:text-green-800">
            <X size={20} />
          </button>
        </div>
      )}

      {/* Formulario */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Proveedor
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Distribuidora ABC"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <span className="notranslate" translate="no">NIT</span> (Número de Identificación Tributaria)
                </label>
                <input
                  type="text"
                  value={formData.nit}
                  onChange={(e) => setFormData({ ...formData, nit: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: 1234567-8"
                  required
                />
              </div>
            </div>

            {isAdmin && (
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="is_global"
                    checked={formData.is_global}
                    onChange={(e) => setFormData({ ...formData, is_global: e.target.checked, store_id: '' })}
                    className="h-4 w-4 text-blue-600 rounded"
                  />
                  <label htmlFor="is_global" className="text-sm font-medium text-gray-700 flex items-center">
                    <Globe size={16} className="mr-2 text-blue-500" />
                    Proveedor Global (disponible para todas las tiendas)
                  </label>
                </div>

                {!formData.is_global && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tienda Específica
                    </label>
                    <select
                      value={formData.store_id}
                      onChange={(e) => setFormData({ ...formData, store_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      required={!formData.is_global}
                    >
                      <option value="">Seleccionar tienda...</option>
                      {stores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
              >
                {submitting ? 'Guardando...' : (editingSupplier ? 'Actualizar' : 'Crear Proveedor')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingSupplier(null);
                }}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Proveedores */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-600">Cargando...</div>
        ) : suppliers.length === 0 ? (
          <div className="p-8 text-center text-gray-600">No hay proveedores registrados</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Proveedor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider notranslate" translate="no">
                    NIT
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alcance
                  </th>
                  {canEdit && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {suppliers.map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{supplier.nit}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {supplier.is_global ? (
                        <span className="px-2 py-1 inline-flex items-center text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          <Globe size={12} className="mr-1" />
                          Global
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex items-center text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          <Building2 size={12} className="mr-1" />
                          {supplier.store_name || 'Tienda específica'}
                        </span>
                      )}
                    </td>
                    {canEdit && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(supplier)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Editar"
                          >
                            <Edit2 size={18} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(supplier.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Desactivar"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Suppliers;
